// FLAGS: none
// The URL language prefix (`/de/…`, `/nl/…`): which first path segment is a language and which full code it stands
// for. Read by the i18next detector and by the boot for the router basename. Pure — no env, window or i18next — so
// it runs before boot and tests without a DOM. With prefix routing on every address gets a prefix, English included.
import {
  LanguageCodeDutchNl,
  LanguageCodeEnglishGb,
  LanguageCodeFrenchFr,
  LanguageCodeGermanDe,
  LanguageCodeSpanishEs,
} from "@src/utils/languageHelpers";

// URL prefix → full language code.
export const LANGUAGE_PATH_PREFIXES: Readonly<Record<string, string>> = {
  nl: LanguageCodeDutchNl,
  en: LanguageCodeEnglishGb,
  de: LanguageCodeGermanDe,
  fr: LanguageCodeFrenchFr,
  es: LanguageCodeSpanishEs,
};

// A Set, so prototype names (`/constructor/…`) are never a prefix.
const PREFIXES = new Set(Object.keys(LANGUAGE_PATH_PREFIXES));

// First path segment as typed; `""` for `/` or `//de/…`. Exported for the miss trace.
const FIRST_SEGMENT = /^\/?([^/]*)/;

export const firstPathSegment = (pathname: string): string =>
  FIRST_SEGMENT.exec(pathname)?.[1] ?? "";

// The lower-cased prefix (`de`) when the first segment is a language, else `undefined`.
export const getLanguagePathPrefix = (pathname: string): string | undefined => {
  const segment = firstPathSegment(pathname).toLowerCase();
  return PREFIXES.has(segment) ? segment : undefined;
};

// `/de/dashboard` → `de-DE`; `undefined` without a prefix.
export const getLanguageForPath = (pathname: string): string | undefined => {
  const prefix = getLanguagePathPrefix(pathname);
  return prefix === undefined ? undefined : LANGUAGE_PATH_PREFIXES[prefix];
};

export const hasLanguagePathPrefix = (pathname: string): boolean =>
  getLanguagePathPrefix(pathname) !== undefined;

// `de-DE` → `de`; `undefined` for a language no prefix stands for.
export const prefixForLanguage = (code: string): string | undefined => {
  const prefix = code.substring(0, 2).toLowerCase();
  return PREFIXES.has(prefix) ? prefix : undefined;
};

// The router basename: `/de` under a prefix with the flag on, otherwise none, so bare URLs mount as before. The
// router matches it case-insensitively, so `/DE/…` still matches.
export const routerBasenameFor = (
  pathname: string,
  languagePathPrefix: boolean,
): string | undefined => {
  if (!languagePathPrefix) {
    return undefined;
  }
  const prefix = getLanguagePathPrefix(pathname);
  return prefix === undefined ? undefined : `/${prefix}`;
};

// `/de/dashboard` → `/dashboard`, `/de` → `/`; unprefixed paths unchanged. Strips only, no normalising.
export const stripLanguagePathPrefix = (pathname: string): string => {
  if (!hasLanguagePathPrefix(pathname)) {
    return pathname;
  }
  const remainder = pathname.replace(FIRST_SEGMENT, "");
  return remainder === "" ? "/" : remainder;
};
